import * as React from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ShieldIcon from "@mui/icons-material/Shield";
import StarIcon from "@mui/icons-material/Star";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { getAuth } from "firebase/auth";

import type {
    ClubInClubNextSessionDto,
    ClubInClubPlanTag,
    ClubInClubPushResponseDto,
} from "@volley/data";

import userMonitor from "../../userMonitor";
import { addWebbySession } from "../../util/addWebbySession";
import fetchApi, {
    FETCH_TIMEOUT_MESSAGE,
    FetchError,
    isAbortError,
    logFetchError,
    timeoutInit,
} from "../../util/fetchApi";
import { navigateTo } from "../../util/navigate";

import {
    useHomeClubInClub,
    type HomeClubInClubValue,
} from "./HomeClubInClubContext";

/**
 * The palette and metrics are lifted from the member portal's own home module
 * (`ClubHomeModule` / `cic.css` in the clubinclub repo) so the two surfaces read as one
 * product — a member tapping through should not notice a seam. Values are inlined rather
 * than themed because they are Club-in-Club's, not Volley's, and should not drift with
 * the rest of the home screen.
 */
const CIC = {
    accent: "#3CE97C",
    accentFg: "#07301a",
    surface: "#243049",
    line: "rgba(255,255,255,0.09)",
    lineStrong: "rgba(255,255,255,0.16)",
    muted: "#94A3B8",
    danger: "#ef4444",
    /** VIP members are picked out in gold, as on the design canvas. */
    vip: "#F5C542",
} as const;

/** Full-bleed row below the balance: next session, alerts. Matches `.cic-next-strip`. */
const STRIP = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    color: "#fff",
    padding: "13px 16px",
    background: "rgba(0,0,0,0.18)",
    borderTop: `1px solid ${CIC.line}`,
    textAlign: "left",
} as const;

/** The 34px rounded glyph tile at the head of a strip. Matches `.cic-next-ic`. */
const STRIP_ICON = {
    width: 34,
    height: 34,
    borderRadius: "10px",
    background: "rgba(60,233,124,0.14)",
    color: CIC.accent,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
} as const;

/** Pill button. Matches `.cic-btn.sm`. */
const PILL = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    height: 42,
    padding: "0 18px",
    borderRadius: "30px",
    fontFamily: "'Fira Sans', sans-serif",
    fontSize: "15px",
    fontWeight: 700,
    transition: "filter 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": { filter: "brightness(1.08)" },
    "&.Mui-disabled": { opacity: 0.5 },
} as const;

/**
 * The day portion of a session label, mirroring the portal's `fmtDayLong`: a relative
 * word where one applies, but always with the concrete date, because "Tomorrow" alone
 * stops being unambiguous the moment a member holds more than one booking.
 */
function formatSessionStart(iso: string, timeZone: string): string {
    const start = new Date(iso);
    const dayKey = (d: Date) =>
        new Intl.DateTimeFormat("en-US", {
            timeZone,
            dateStyle: "medium",
        }).format(d);
    const date = new Intl.DateTimeFormat("en-US", {
        timeZone,
        month: "short",
        day: "numeric",
    }).format(start);
    const time = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour: "numeric",
        minute: "2-digit",
    }).format(start);

    const now = new Date();
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    if (dayKey(start) === dayKey(now)) return `Today · ${date} ${time}`;
    if (dayKey(start) === dayKey(tomorrow)) return `Tomorrow · ${date} ${time}`;

    const weekday = new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "short",
    }).format(start);
    return `${weekday}, ${date} ${time}`;
}

/**
 * "Tomorrow · Sep 12 4:00 PM · Court #5".
 *
 * The court matters because the machine moves between them, so this is what tells a
 * member where to go; it is simply absent when CourtReserve could not name it.
 */
export function sessionLabel(
    session: ClubInClubNextSessionDto,
    timeZone: string,
): string {
    const when = formatSessionStart(session.startTime, timeZone);
    return session.courtName ? `${when} · ${session.courtName}` : when;
}

/**
 * "Resets on Oct 18": when the member's membership points are replaced by the next
 * month's. They reset rather than expire -- the spec keeps "expire" for flex points,
 * which run on their own 90-day clock -- and a VIP's reset the same way, on the 1st.
 *
 * A member who has cancelled is the exception, "Expires on": the date is then the end of
 * the period they paid for, when the spec's cancel sheet says their membership points
 * expire, and nothing replaces them.
 *
 * Club-in-Club reports the date as a number of days rather than a date, so it is
 * reconstructed here and formatted in the club's timezone — the calendar the member
 * is standing in — not the device's. Absent (null) when there is no cycle to reset at,
 * i.e. before the member has activated: their signup points have no clock running.
 */
export function pointsExpiryLabel(
    refreshInDays: number | null,
    timeZone: string,
    now: Date = new Date(),
    verb: "Resets on" | "Expires on" = "Resets on",
): string | null {
    if (refreshInDays == null) return null;
    const expires = new Date(
        now.getTime() + refreshInDays * 24 * 60 * 60 * 1000,
    );
    const date = new Intl.DateTimeFormat("en-US", {
        timeZone,
        month: "short",
        day: "numeric",
    }).format(expires);
    return `${verb} ${date}`;
}

/** A screen in the portal, and any parameter it needs to identify what to show. */
export interface ClubInClubTarget {
    screen: string;
    params?: Record<string, string>;
}

/**
 * Where a tap should send the member: the portal origin the server gave us, carrying a
 * freshly minted Firebase custom token and — when the row points somewhere specific —
 * the screen to open on.
 *
 * Club-in-Club is a separate origin sharing Volley's Firebase directory, so it needs a
 * session of its own. Its entrypoint reads `?token=`, exchanges it via
 * `signInWithCustomToken`, and strips it from the URL — the same handoff the iOS
 * RobotController webview already uses. `?screen=` rides along and is consumed the same
 * way: the portal has no router, so it seeds its navigation stack from this rather than
 * from a path, which is what lets every row here land somewhere useful instead of on the
 * portal's home screen. It validates the name against its own allowlist, so an
 * unrecognised one simply opens home. `?webby_session=` links the portal's RUM session
 * back to ours when we have one.
 */
export function clubInClubHandoffUrl(
    portalUrl: string,
    token: string,
    target?: ClubInClubTarget,
): string {
    const url = new URL("/", portalUrl);
    url.searchParams.set("token", token);
    addWebbySession(url);
    if (target) {
        url.searchParams.set("screen", target.screen);
        for (const [key, value] of Object.entries(target.params ?? {})) {
            url.searchParams.set(key, value);
        }
    }
    return url.href;
}

function Strip({
    icon,
    eyebrow,
    title,
    subtitle,
    tone = "normal",
    onClick,
    busy = false,
    announce = false,
}: {
    icon: React.ReactNode;
    /** Small uppercase label above the title, as the next-session row uses. */
    eyebrow?: string;
    title: string;
    subtitle?: string;
    tone?: "normal" | "danger";
    onClick?: () => void;
    /** Working on the last tap. Stays a focusable button, marked busy, rather than turning
     *  into a plain box that drops keyboard focus. */
    busy?: boolean;
    /** Read the subtitle out when it changes, for one that reports what a tap did. */
    announce?: boolean;
}): React.JSX.Element {
    const danger = tone === "danger";
    const body = (
        <>
            <Box
                sx={{
                    ...STRIP_ICON,
                    ...(danger && {
                        background: "rgba(239,68,68,0.2)",
                        color: CIC.danger,
                    }),
                }}
            >
                {icon}
            </Box>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                {eyebrow && (
                    <Typography
                        sx={{
                            fontSize: "11px",
                            color: CIC.muted,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            fontWeight: 700,
                        }}
                    >
                        {eyebrow}
                    </Typography>
                )}
                <Typography
                    sx={{ color: "#fff", fontWeight: 700, fontSize: "14.5px" }}
                >
                    {title}
                </Typography>
                {subtitle && (
                    <Typography
                        sx={{ color: CIC.muted, fontSize: "12.5px" }}
                        aria-live={announce ? "polite" : undefined}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Stack>
            {onClick && (
                <ChevronRightIcon sx={{ color: CIC.muted, fontSize: 20 }} />
            )}
        </>
    );

    const sx = {
        ...STRIP,
        ...(danger && {
            background: "rgba(239,68,68,0.15)",
            borderTop: `1px solid ${CIC.danger}`,
        }),
    };

    return onClick ? (
        <ButtonBase
            onClick={onClick}
            sx={sx}
            aria-busy={busy || undefined}
            aria-disabled={busy || undefined}
        >
            {body}
        </ButtonBase>
    ) : (
        <Box sx={sx}>{body}</Box>
    );
}

/** The activation card's heading and button, verbatim from the activation handoff. */
export const ACTIVATE_LABEL = "Activate your Volley membership";

/**
 * The prompt for someone who hasn't activated (IPC activation handoff, 2026-10): heading,
 * one line, and a button, with a small non-lock icon. Prominent, but the rest of home stays
 * as browsable as ever: nothing here blurs, locks or blocks anything below it. The choice
 * itself (I'm VIP, or the free month) is Club-in-Club's Welcome screen, which the button
 * opens.
 */
function ActivationCard({
    club,
    busy,
    busyLabel,
    problem,
    onActivate,
}: {
    club: string | null;
    busy: boolean;
    busyLabel: string;
    /** Why the last tap didn't get through, phrased for the member. */
    problem: string | null;
    /** Absent when there is no portal to hand off to. */
    onActivate?: () => void;
}): React.JSX.Element {
    return (
        <Stack
            alignItems="center"
            spacing={1.25}
            sx={{ padding: "26px 18px 18px", textAlign: "center" }}
        >
            <AutoAwesomeIcon sx={{ fontSize: 34, color: CIC.accent }} />
            <Typography
                component="h2"
                sx={{
                    color: "#fff",
                    fontFamily: "Gotham, 'Arial Black', sans-serif",
                    fontWeight: 900,
                    fontSize: "24px",
                    lineHeight: 1.15,
                    margin: 0,
                }}
            >
                {ACTIVATE_LABEL}
            </Typography>
            <Typography
                sx={{ color: "#C7D0E0", fontSize: "15px", lineHeight: 1.45 }}
            >
                {club
                    ? `Activate your membership to access Volley at ${club}.`
                    : "Activate your membership to access Volley."}
            </Typography>
            <ButtonBase
                disabled={!onActivate}
                onClick={onActivate}
                aria-busy={busy || undefined}
                aria-disabled={busy || undefined}
                sx={{
                    ...PILL,
                    width: "100%",
                    marginTop: "8px !important",
                    height: 46,
                    background: CIC.accent,
                    color: CIC.accentFg,
                }}
            >
                {busy ? busyLabel : ACTIVATE_LABEL}
            </ButtonBase>
            {problem && (
                <Typography
                    aria-live="polite"
                    sx={{
                        color: CIC.muted,
                        fontSize: "12.5px",
                        lineHeight: 1.4,
                    }}
                >
                    {problem}
                </Typography>
            )}
        </Stack>
    );
}

/**
 * The tag beside the balance: which membership this is, matching the one Club-in-Club shows
 * on its booking home. Older Club-in-Club deployments send no planTag; a VIP flag still
 * gets its badge then. A null planTag doesn't override it either: isVip is the effective
 * answer, so a VIP keeps the badge even if the tag says otherwise.
 */
export function planTagLabel(
    planTag: ClubInClubPlanTag | null | undefined,
    isVip: boolean,
): { label: string; vip: boolean } | null {
    if (planTag === "vip" || (planTag == null && isVip)) {
        return { label: "VIP member", vip: true };
    }
    if (planTag === "trial") return { label: "Free trial", vip: false };
    if (planTag === "member") return { label: "Volley member", vip: false };
    return null;
}

/**
 * What the set-up banner says when the membership couldn't be set up: what the person can
 * do about it, and "try again" only where trying again can help. `club` is the pilot
 * club's name, as Club-in-Club gave it for this banner.
 */
export function setUpRefusal(
    outcome: ClubInClubPushResponseDto["outcome"],
    club: string | null,
): string {
    switch (outcome) {
        case "no_home_location":
            return club
                ? `Volley membership is only at ${club} for now. Make it your home location in your profile to join.`
                : "Volley membership isn't available at your club yet.";
        // Also what a club mapping that's wrong on our side looks like, for someone whose
        // home location is already the right club: hence the second half.
        case "location_not_enabled":
            return club
                ? `Volley membership is only at ${club} for now. Make it your home location in your profile to join, or contact support if it already is.`
                : "Volley membership isn't available at your club yet.";
        case "needs_link":
            return club
                ? `There's already a membership at ${club} with your email. Contact support to connect it to this account.`
                : "There's already a membership with your email. Contact support to connect it to this account.";
        case "identity_conflict":
            return "Your Volley membership is set up at another club. Contact support if that's not right.";
        case "invalid_profile":
            return "Add your first and last name to your profile, then tap to try again.";
        case "account_not_found":
        case "account_disabled":
        case "no_firebase_account":
            return "We couldn't set up a membership for this account. Contact support for help.";
        case "unavailable":
        case "disabled":
            return "Membership set-up isn't available right now. Please try again later.";
        default:
            return "Couldn't set up your membership just now. Tap to try again.";
    }
}

/**
 * What the banner says when the push itself failed, rather than Club-in-Club saying no.
 *
 * A timeout gets the app's own timeout wording: the member may well have been created, and
 * the next tap will answer `exists`. A 4xx is webby-server refusing this account (a feature
 * list the app cached before the account left the cohort, say), which no retry fixes; its
 * message is written for us rather than for a member, so it isn't shown. Anything else --
 * a 5xx, a dropped connection -- is worth another tap.
 */
export function setUpFailure(err: unknown, club: string | null): string {
    if (isAbortError(err)) return FETCH_TIMEOUT_MESSAGE;
    if (
        err instanceof FetchError &&
        err.statusCode >= 400 &&
        err.statusCode < 500
    ) {
        return setUpRefusal("account_not_found", club);
    }
    return setUpRefusal("unreachable", club);
}

/** What the activation card says when the handoff to Club-in-Club didn't get there. */
export const OPEN_FAILURE =
    "Couldn't open Club-in-Club just now. Tap to try again.";

/** The banner's subtitle while its push is in flight. */
export const SETTING_UP_SUBTITLE = "This can take a few seconds.";

/**
 * How long the banner waits for its push. Longer than fetchApi's 10s default, and longer
 * than webby-server's own budget for the push (CLUB_IN_CLUB_PROVISION_TIMEOUT_MS, 20s), so
 * a slow Club-in-Club reads as the answer webby-server gives rather than as our timeout while
 * the member is still being created.
 */
const SET_UP_WAIT_MS = 25_000;

/**
 * Whether the widget will render anything at all.
 *
 * Exported because the home screen has to know before it decides who owns the top slot:
 * the widget hides itself for anyone with neither a member record nor a way to create
 * one, and swapping Ask Volley out for something invisible leaves the screen headed by
 * nothing. False while loading — not "no" yet, just not "yes".
 */
export function clubInClubWidgetRenders(value: HomeClubInClubValue): boolean {
    if (value.loading) return false;
    const { member, setup } = value.status;
    return member != null || setup?.state === "ready";
}

/**
 * Club-in-Club on the home screen: point balance, next reserved court session,
 * membership alerts, and a way into the member portal.
 *
 * A persistent section rather than a carousel slide: it carries live numbers and
 * actions, and should not be swipeable past. It renders nothing at all for anyone who
 * has neither a member record nor the ability to create one, which is nearly every
 * Volley user given the club is one pilot location.
 */
export default function ClubInClubWidget(): React.JSX.Element | null {
    const { status, loading, refetch } = useHomeClubInClub();
    const [opening, setOpening] = React.useState(false);
    // The state drives the card; the ref is what stops a second tap, as `setUpInFlight`
    // does for the push.
    const openInFlight = React.useRef(false);
    // Set once the handoff has navigated, so only a return from the portal counts as
    // one: a focus event while the token is still being minted doesn't.
    const handedOff = React.useRef(false);
    // Why the last handoff didn't get as far as Club-in-Club. Shown on the activation
    // card, where a tap that silently did nothing reads as a broken button.
    const [openProblem, setOpenProblem] = React.useState<string | null>(null);
    // The set-up banner's push: in flight, and why it didn't go through when it didn't.
    // `settingUp` drives the banner; the ref is what stops a second tap, since two taps
    // can land before the first one's state update renders.
    const [settingUp, setSettingUp] = React.useState(false);
    const [setUpProblem, setSetUpProblem] = React.useState<string | null>(null);
    const setUpInFlight = React.useRef(false);
    const setUpTapped = React.useRef(false);

    // A push or a token mint can outlive the card: the member taps it and then leaves
    // home. Its answer must not then pull them into Club-in-Club from wherever they went.
    const mounted = React.useRef(true);
    React.useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const { member, portalUrl, setup } = status;
    const club = setup?.locationName ?? null;

    // Leaving for the portal normally tears this page down, so `opening` never needed
    // clearing. It does not in the native app: the portal loads in a drawer over the
    // dashboard, and closing it returns to a page that never unmounted -- with the card
    // still latched disabled until the screen is navigated away from and back. Nor is
    // the status re-read, so a member who just activated there would still be asked to
    // activate: hence the quiet refetch, which re-reads without blanking the card.
    //
    // Three signals because no single one covers every way back: `pageshow` fires on a
    // bfcache restore, `visibilitychange` when a backgrounded webview returns, and
    // `focus` when a drawer closes over a page that was never hidden.
    React.useEffect(() => {
        if (!opening) return undefined;
        const reset = () => {
            if (!handedOff.current) return;
            handedOff.current = false;
            openInFlight.current = false;
            setOpening(false);
            void refetch({ quiet: true });
        };
        const onVisible = () => {
            if (document.visibilityState === "visible") reset();
        };
        window.addEventListener("pageshow", reset);
        window.addEventListener("focus", reset);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener("pageshow", reset);
            window.removeEventListener("focus", reset);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [opening, refetch]);

    const open = React.useCallback(
        async (action: string, target?: ClubInClubTarget) => {
            if (!portalUrl || openInFlight.current) return;
            openInFlight.current = true;
            setOpening(true);
            setOpenProblem(null);
            userMonitor.addAction("club_in_club_open", { action });
            // Who the token will be minted for. Checked again below: a member who
            // switches account mid-flight must not be handed the previous one's session.
            const signedInAs = getAuth().currentUser;
            try {
                // no-store: a custom token is single-use and short-lived, so a cached
                // response would hand the portal a credential that is already spent.
                const { token } = await fetchApi<{ token: string }>(
                    "/api/users/token",
                    "GET",
                    undefined,
                    { cache: "no-store" },
                );
                if (!mounted.current) {
                    openInFlight.current = false;
                    return;
                }
                if (getAuth().currentUser !== signedInAs) {
                    openInFlight.current = false;
                    setOpening(false);
                    return;
                }
                if (typeof token !== "string" || !token.trim()) {
                    throw new Error("Missing handoff token");
                }
                // In place, not a new tab: the portal shows a "← Back to Volley" link in
                // this webview and replaces (not pushes) the cleaned URL, so both it and
                // webview-back return to this screen. A new tab becomes a Safari sheet
                // with its own session and no return path.
                handedOff.current = true;
                navigateTo(clubInClubHandoffUrl(portalUrl, token, target));
            } catch (err) {
                logFetchError(err, "Could not open the Club-in-Club portal");
                openInFlight.current = false;
                if (mounted.current) {
                    setOpening(false);
                    setOpenProblem(OPEN_FAILURE);
                }
            }
        },
        [portalUrl],
    );

    // Club-in-Club creates members only when Volley pushes them, so the activation card makes
    // that push itself -- the same one signup and the admin button make -- and only then
    // hands off to Club-in-Club's Welcome screen, which by then knows them.
    const setUp = React.useCallback(async () => {
        if (setUpInFlight.current) return;
        setUpInFlight.current = true;
        setSettingUp(true);
        setSetUpProblem(null);
        setOpenProblem(null);
        // One "selected" per visit to home, as when a tap always left the screen; a tap
        // after a refusal is a retry.
        userMonitor.addAction(
            setUpTapped.current
                ? "membership_setup_retried"
                : "membership_banner_selected",
            {},
        );
        setUpTapped.current = true;
        // Who the member is being set up for. A switch of account mid-flight drops the
        // answer, as `open` does for its token.
        const signedInAs = getAuth().currentUser;
        let problem: string | null = null;
        try {
            const { outcome } = await fetchApi<ClubInClubPushResponseDto>(
                "/api/integrations/club-in-club/me/provision",
                "POST",
                undefined,
                timeoutInit(SET_UP_WAIT_MS),
            );
            if (!mounted.current) return;
            if (getAuth().currentUser !== signedInAs) {
                setSettingUp(false);
                return;
            }
            if (outcome === "created" || outcome === "exists") {
                // open() says why itself when the handoff fails.
                await open("activate", { screen: "activate" });
                if (mounted.current) setSettingUp(false);
                return;
            }
            userMonitor.addAction("membership_setup_refused", { outcome });
            problem = setUpRefusal(outcome, club);
        } catch (err) {
            logFetchError(err, "Could not set up the Club-in-Club membership");
            problem = setUpFailure(err, club);
        } finally {
            setUpInFlight.current = false;
        }
        if (!mounted.current) return;
        setSetUpProblem(problem);
        setSettingUp(false);
    }, [open, club]);

    // Withhold while the answer could still change, and stay absent for the large
    // majority who have neither an account nor a way to create one.
    if (loading) return null;
    if (!member && setup?.state !== "ready") return null;

    // The card itself. `overflow: hidden` is what lets the strips run full-bleed to the
    // rounded edge, and the radial wash is the portal's signature.
    const card = {
        margin: "6px 0 18px",
        borderRadius: "18px",
        overflow: "hidden",
        background: `radial-gradient(130% 100% at 100% 0%, rgba(60,233,124,0.14), transparent 60%), ${CIC.surface}`,
        boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
    };

    if (!member) {
        // The way in for someone who isn't a member yet: create them in Club-in-Club (see
        // setUp), then hand off to its Welcome screen. Gated on a portal, since the choice
        // lives there; a tap while the push is in flight does nothing.
        return (
            <Box sx={card}>
                <ActivationCard
                    club={club}
                    busy={settingUp || opening}
                    busyLabel={
                        settingUp ? "Setting up your membership…" : "Opening…"
                    }
                    // Neutral words while a push is in flight, so a retry doesn't read the
                    // last refusal out again.
                    problem={
                        settingUp
                            ? SETTING_UP_SUBTITLE
                            : (setUpProblem ?? openProblem)
                    }
                    onActivate={portalUrl ? () => void setUp() : undefined}
                />
            </Box>
        );
    }

    const next = status.nextSession;
    // A VIP tapped "I'm VIP" in the portal: no card, no subscription, never charged.
    const vip = member.isVip === true;
    const expiry = pointsExpiryLabel(
        status.refreshInDays,
        member.timezone,
        undefined,
        member.subscriptionStatus === "canceled" ? "Expires on" : "Resets on",
    );
    const suspended = member.status === "suspended";
    const pastDue = member.subscriptionStatus === "past_due";
    const noPlan = member.subscriptionStatus === null;
    // A member who has never activated: no plan, not VIP. Choosing either plan activates
    // them, the Volley membership by creating a subscription and "I'm VIP" by setting
    // isVip. They get the activation card instead of a balance they can't use yet.
    const preActivation = !suspended && !pastDue && noPlan && !vip;
    if (preActivation) {
        return (
            <Box sx={card}>
                <ActivationCard
                    club={member.locationName}
                    busy={opening}
                    busyLabel="Opening…"
                    problem={openProblem}
                    onActivate={
                        portalUrl
                            ? () => {
                                  userMonitor.addAction(
                                      "membership_activate_selected",
                                      {},
                                  );
                                  void open("activate", { screen: "activate" });
                              }
                            : undefined
                    }
                />
            </Box>
        );
    }
    const tag = planTagLabel(member.planTag, vip);

    return (
        <Box sx={card}>
            {/* Balance. `space-between` leaves the trailing slot free, as the
                    portal's own layout does. */}
            <Box
                sx={{
                    padding: "16px 16px 14px",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "12px",
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            textTransform: "uppercase",
                            fontSize: "12px",
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            color: CIC.accent,
                        }}
                    >
                        Points
                    </Typography>
                    <Stack
                        direction="row"
                        alignItems="baseline"
                        gap="8px"
                        sx={{ marginTop: "4px" }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: "Gotham, 'Arial Black', sans-serif",
                                fontWeight: 900,
                                fontSize: "40px",
                                lineHeight: 1,
                                color: "#fff",
                            }}
                        >
                            {status.pointBalance ?? 0}
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                color: CIC.muted,
                                fontWeight: 700,
                                fontSize: "14px",
                            }}
                        >
                            points
                        </Typography>
                    </Stack>
                    {/* The spec wants balance and expiry visible together wherever a
                        member books. Absent before the free month starts, when there
                        is no cycle to expire at. */}
                    {expiry && (
                        <Typography
                            sx={{
                                color: CIC.muted,
                                fontSize: "13px",
                                fontWeight: 500,
                                marginTop: "6px",
                            }}
                        >
                            {expiry}
                        </Typography>
                    )}
                </Box>
                {tag && (
                    <Typography
                        component="span"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            flexShrink: 0,
                            fontSize: "11px",
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: tag.vip ? CIC.vip : CIC.accent,
                            padding: "6px 10px",
                            border: tag.vip
                                ? "1px solid rgba(245,197,66,0.5)"
                                : "1px solid rgba(60,233,124,0.5)",
                            borderRadius: "999px",
                        }}
                    >
                        {tag.vip && <StarIcon sx={{ fontSize: 12 }} />}
                        {tag.label}
                    </Typography>
                )}
            </Box>

            {(suspended || pastDue) && (
                <Strip
                    tone="danger"
                    icon={<ShieldIcon sx={{ fontSize: 18 }} />}
                    title="Membership paused — payment failed"
                    subtitle="Update your card to reactivate and book"
                    onClick={
                        portalUrl
                            ? () =>
                                  void open("billing", {
                                      screen: "account",
                                  })
                            : undefined
                    }
                />
            )}
            {/* The portal owns booking, so this is an entry point into it rather than a
                    flow of its own. Absent until tap-through is switched on server-side,
                    because a button that cannot do anything is worse than no button.

                    There is no "Add points" beside it. The spec offers buying only inside
                    booking, where the portal knows the shortfall; on the home screen it
                    was a second way to spend money with nothing to spend it on. */}
            {portalUrl && (
                <Box
                    sx={{
                        display: "flex",
                        gap: "10px",
                        padding: "0 14px 14px",
                    }}
                >
                    <ButtonBase
                        disabled={opening}
                        onClick={() => void open("book", { screen: "book" })}
                        sx={{
                            ...PILL,
                            flex: 1,
                            background: CIC.accent,
                            color: CIC.accentFg,
                        }}
                    >
                        <CalendarMonthIcon sx={{ fontSize: 17 }} />
                        Book a session
                    </ButtonBase>
                </Box>
            )}

            {next ? (
                <>
                    <Strip
                        icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
                        eyebrow="Next session"
                        title={sessionLabel(next, member.timezone)}
                        onClick={
                            portalUrl
                                ? () =>
                                      void open("reservation", {
                                          screen: "reservation",
                                          params: {
                                              id: next.seatBookingId,
                                          },
                                      })
                                : undefined
                        }
                    />
                    {status.upcomingSessionCount > 1 && (
                        <ButtonBase
                            onClick={
                                portalUrl
                                    ? () =>
                                          void open("reservations", {
                                              screen: "reservations",
                                          })
                                    : undefined
                            }
                            disabled={!portalUrl}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "6px",
                                width: "100%",
                                padding: "11px 16px",
                                background: "rgba(0,0,0,0.10)",
                                borderTop: `1px solid ${CIC.line}`,
                                color: CIC.accent,
                                fontFamily: "'Fira Sans', sans-serif",
                                fontWeight: 700,
                                fontSize: "13px",
                            }}
                        >
                            {`View all ${status.upcomingSessionCount} upcoming sessions`}
                            <ArrowForwardIcon sx={{ fontSize: 15 }} />
                        </ButtonBase>
                    )}
                </>
            ) : (
                <Typography
                    sx={{
                        color: CIC.muted,
                        fontSize: "13px",
                        lineHeight: 1.5,
                        padding: "12px 18px 16px",
                    }}
                >
                    No upcoming sessions yet.
                </Typography>
            )}
        </Box>
    );
}
